'use client'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Product, ProductSort_Field, SortOrder } from '@/eshop/gql/graphql'
import { ChangeEventHandler, useCallback, useContext, useMemo } from 'react'
import { BasketContext, getBasketProductFromProduct } from '../BasketProvider'
import BasketPlusMinus from '../BasketPlusMinus'
import React from 'react'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { useFormatPrice, useProductVariants } from '@/eshop/utils/price'
import { getProductsForGroup } from '@/eshop/queries'
import { useQuery } from '@tanstack/react-query'
import { Option } from '@swan-io/boxed'
import useResponsive from '@/hooks/useResponsive'
import { useUser } from '@/eshop/hooks/useUser'
import { hydrateResult } from '@/eshop/utils/client'
import { LanguageDropdown } from '@/eshop/components/ProductList/LanguageDropdown'
import { getAvailabilityWrapper } from '../ProductList/functions'

interface Props {
    product: Product
    className?: string
}

export function BasketTable({ product, className }: Props) {
    const { t } = useTranslation()
    const { products, addProduct, removeProduct } = useContext(BasketContext)
    const k2 = useK2Setup()
    const format = useFormatPrice(k2.currencyCode)
    const { isDesktop } = useResponsive()
    const user = useUser()

    const group = product.productGroup
    const groupName = group?.sortingNameLang || ''
    const info = useMemo(
        () =>
            product.productLangItems.find(
                (pl) => pl.languageId === k2.languageId
            ),
        [product, k2]
    )

    const manufacturer = useMemo(() => info?.brandName ?? null, [info])

    const variants = useProductVariants(groupName, null, manufacturer)
    const { data: productsInGroup, isFetched: isProductsInGroupFetched } =
        useQuery({
            queryKey: [
                'product',
                product.id,
                'variants',
                group,
                groupName,
                k2.currency,
                k2.languageId,
            ],
            queryFn: () =>
                getProductsForGroup({
                    after: '',
                    before: '',
                    first: 1000,
                    last: 10000,
                    filter: [
                        {
                            productGroup_id: { eq: group?.id },
                            productLangItems_languageId: {
                                eq: k2.languageId,
                            },
                            safetyDataSheets_languageId: {
                                eqOrNull: k2.languageId,
                            },
                            productPrices_currency: {
                                eq: k2.currency,
                            },
                        },
                    ],
                    sort: [
                        {
                            field: ProductSort_Field.Abbr,
                            order: SortOrder.Asc,
                        },
                    ],
                }).toPromise(),
        })

    const productsWQuality = useMemo(() => {
        return (
            hydrateResult(productsInGroup)?.match?.({
                Ok: (d) =>
                    d.products?.edges
                        ?.filter((p) => {
                            const lang = p.node.productLangItems.find(
                                (pl) => pl.languageId === k2.languageId
                            )
                            return !!lang && lang.CFQuality === info?.CFQuality
                        })
                        ?.map((p) => ({
                            ...p,
                            price: variants.data?.match?.({
                                Ok: (d) => {
                                    return (
                                        d.products?.find(
                                            (v) =>
                                                v?.abbr?.replace(/\\/g, '/') ===
                                                p.node.abbr?.replace(/\\/g, '/')
                                        ) || undefined
                                    )
                                },
                                Error: () => undefined,
                            }),
                        })) || [],

                Error: () => [],
            }) || []
        )
    }, [productsInGroup, k2, info, variants])

    const findProductByAbbr = useCallback(
        (abbr: string | null | undefined) => {
            if (!abbr) {
                return undefined
            }
            return (
                hydrateResult(productsInGroup)?.match?.({
                    Ok: (d) =>
                        d.products?.edges?.find((p) => p.node.abbr === abbr),

                    Error: () => undefined,
                }) || undefined
            )
        },
        [productsInGroup]
    )

    const sum = useMemo(
        () =>
            products?.reduce(
                (acc, p) => {
                    const pp = productsWQuality.find(
                        (pwq) => pwq.price?.abbr === p.catNo
                    )
                    if (!pp) {
                        return acc
                    }
                    return [
                        acc[0] + parseFloat(pp.price?.priceNetto) * p.count,
                        acc[1] + parseFloat(pp.price?.priceBrutto) * p.count,
                    ]
                },
                [0, 0]
            ) || [0, 0],
        [products, productsWQuality]
    )

    const getProductInGroupByCatNo = useCallback(
        (catNo: string) => {
            return Option.fromNullable(
                productsInGroup?.match?.({
                    Ok: (d) =>
                        d.products.edges?.find((p) => p.node.abbr === catNo),
                    Error: () => undefined,
                })
            )
        },
        [productsInGroup]
    )

    const handleToggleProductBasketByCatNo = useCallback(
        (
            catNo: string,
            priceNetto: string,
            priceBrutto: string
        ): ChangeEventHandler<HTMLInputElement> =>
            (e) => {
                const isChecked = e.target.checked
                if (!priceNetto || !priceBrutto) {
                    e.target.checked = false
                    return
                }

                getProductInGroupByCatNo(catNo)
                    .map((p) => {
                        const priceId = p.node.productPrices?.[0]?.id
                        return {
                            product: p.node,
                            priceId,
                        }
                    })
                    .flatMap(({ product, priceId }) =>
                        getBasketProductFromProduct(
                            product,
                            priceId,
                            1,
                            user?.id || null,
                            priceNetto,
                            priceBrutto
                        )
                    )
                    .match({
                        Some: (bp) => {
                            if (isChecked) {
                                return addProduct(bp)
                            }
                            return removeProduct(bp)
                        },
                        None: () => {
                            console.log(
                                'ERROR: cannot convert data to basket product'
                            )
                        },
                    })
            },
        [getProductInGroupByCatNo, user?.id, removeProduct, addProduct]
    )

    const isProductInBasketByCatNo = useCallback(
        (catNo: string) => !!products.find((p) => p.catNo === catNo),
        [products]
    )

    return (
        <div className={clsx(!isDesktop && 'overflow-x-scroll', className)}>
            <table className={clsx('table ', !isDesktop && 'min-w-full w-max')}>
                <thead>
                    <tr className="font-bold text-secondary border-0 uppercase text-sm">
                        <th>{t('eshop.productlist.table.content')}</th>
                        <th className="text-right text-secondary uppercase">
                            {t('eshop.productlist.table.price')}{' '}
                            <span className="text-xs font-normal">
                                ({t('eshop.price.baseSuffix')})
                            </span>
                        </th>
                        <th className="text-right text-secondary uppercase">
                            {t('eshop.productlist.table.price')}{' '}
                            <span className="text-xs font-normal">
                                ({t('eshop.price.vatSuffix')})
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody className="text-base">
                    {!variants.isFetched && (
                        <tr>
                            <td colSpan={3}>
                                <div className="flex justify-center my-4">
                                    <span className="loading loading-spinner loading-lg"></span>
                                </div>
                            </td>
                        </tr>
                    )}
                    {variants.isFetched &&
                        productsWQuality.map((p) => {
                            const catNo = p.node.abbr
                            if (!catNo) {
                                return null
                            }

                            const isInBasket = isProductInBasketByCatNo(catNo)
                            const info = p.node.productLangItems.find(
                                (pl) => pl.languageId === k2.languageId
                            )

                            const isOpenedProduct = product.abbr === p.node.abbr

                            // if we dont have price and its not the main product, we omit item from table
                            if (
                                (!p.price?.priceNetto ||
                                    !p.price?.priceBrutto) &&
                                p.node.id !== product.id
                            ) {
                                return null
                            }

                            const availability = getAvailabilityWrapper(
                                p.price?.available ?? 'Unavailable',
                                p.price?.colorHexTxt ?? 'black',
                                p.price?.availableInStore ?? '',
                                p.price?.colorHexTxtInStore ?? 'black'
                            )

                            const isDetailProductWOPrice =
                                (!p.price?.priceNetto ||
                                    !p.price?.priceBrutto) &&
                                p.node.id === product.id

                            const pp = findProductByAbbr(p.node.abbr)

                            return (
                                <React.Fragment key={catNo}>
                                    <tr className={clsx('rounded-lg border-0')}>
                                        <td
                                            className={clsx(
                                                '  rounded-tl-lg',
                                                !isInBasket && 'rounded-bl-lg',
                                                isOpenedProduct
                                                    ? 'bg-cyan-50'
                                                    : 'bg-gray-light',
                                                isOpenedProduct &&
                                                    isInBasket &&
                                                    'border-b-0'
                                            )}
                                        >
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    onChange={
                                                        isDetailProductWOPrice
                                                            ? undefined
                                                            : handleToggleProductBasketByCatNo(
                                                                  catNo,
                                                                  p.price
                                                                      ?.priceNetto,
                                                                  p.price
                                                                      ?.priceBrutto
                                                              )
                                                    }
                                                    checked={isInBasket}
                                                    disabled={
                                                        isDetailProductWOPrice
                                                    }
                                                    className="checkbox bg-white checkbox-info tooltip"
                                                    data-tip={
                                                        isInBasket
                                                            ? 'Odebrat z košíku'
                                                            : 'Přidat do košíku'
                                                    }
                                                />
                                                {info?.unit}
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-sm  mt-1">
                                                    {p.node.abbr}
                                                </span>
                                                {p.node.abbr2 &&
                                                    p.node.abbr2 !==
                                                        p.node.abbr && (
                                                        <span className="text-xs text-gray-500 mt-1">
                                                            {p.node.abbr2}
                                                        </span>
                                                    )}
                                                <span className="text-sm font-semibold">
                                                    {
                                                        p.node
                                                            .productLangItems[0]
                                                            ?.languageTextCalc
                                                    }
                                                </span>
                                                <div
                                                    className="text-sm"
                                                    style={{
                                                        color: availability.main
                                                            ?.colorHexTxt,
                                                    }}
                                                >
                                                    {
                                                        availability.main
                                                            ?.available
                                                    }
                                                </div>
                                                <div
                                                    className="text-sm"
                                                    style={{
                                                        color: availability
                                                            .external
                                                            ?.colorHexTxt,
                                                    }}
                                                >
                                                    {
                                                        availability.external
                                                            ?.available
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            className={clsx(
                                                'text-center align-text-top',
                                                isOpenedProduct
                                                    ? 'bg-cyan-50'
                                                    : 'bg-gray-light',
                                                isOpenedProduct &&
                                                    isInBasket &&
                                                    'border-b-0'
                                            )}
                                        >
                                            {p.price?.priceNetto
                                                ? format(p.price?.priceNetto)
                                                : '---'}
                                        </td>
                                        <td
                                            className={clsx(
                                                'rounded-tr-lg text-right align-text-top',
                                                !isInBasket && 'rounded-br-lg',
                                                isOpenedProduct
                                                    ? 'bg-cyan-50'
                                                    : 'bg-gray-light',
                                                isOpenedProduct &&
                                                    isInBasket &&
                                                    'border-b-0'
                                            )}
                                        >
                                            {p.price?.priceBrutto
                                                ? format(p.price?.priceBrutto)
                                                : '---'}
                                            <div className="flex justify-end items-end mt-3">
                                                <div
                                                    className={clsx(
                                                        'link link-hover flex justify-center items-center tooltip dropdown',
                                                        !!product.specifications &&
                                                            product
                                                                .specifications
                                                                ?.length > 0
                                                            ? ' text-blue-400 cursor-pointer'
                                                            : 'text-gray-500 cursor-not-allowed'
                                                    )}
                                                    data-tip={t(
                                                        'eshop.productlist.table.specifications'
                                                    )}
                                                >
                                                    <div
                                                        tabIndex={0}
                                                        role="button"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="h-6"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                                                            />
                                                        </svg>
                                                    </div>

                                                    <LanguageDropdown
                                                        data={
                                                            pp?.node
                                                                ?.specifications ??
                                                            []
                                                        }
                                                        tabIndex={0}
                                                    />
                                                </div>
                                                <div
                                                    className="link link-hover text-blue-400 flex justify-center items-center tooltip dropdown"
                                                    data-tip={t(
                                                        'eshop.productlist.table.safetySheets'
                                                    )}
                                                >
                                                    <div
                                                        tabIndex={1}
                                                        role="button"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="h-6"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375a9 9 0 0 1 9 9v.375M10.125 2.25A3.375 3.375 0 0 1 13.5 5.625v1.5c0 .621.504 1.125 1.125 1.125h1.5a3.375 3.375 0 0 1 3.375 3.375M9 15l2.25 2.25L15 12"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <LanguageDropdown
                                                        data={
                                                            pp?.node
                                                                ?.safetyDataSheets ??
                                                            []
                                                        }
                                                        tabIndex={1}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>

                                    {isInBasket && !isDetailProductWOPrice && (
                                        <tr className="border-0">
                                            <td
                                                colSpan={2}
                                                className={clsx(
                                                    'items-center gap-2 rounded-bl-lg pt-0',
                                                    isOpenedProduct
                                                        ? 'border-0 bg-cyan-50'
                                                        : 'border-0 bg-gray-light'
                                                )}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="text-base">
                                                        {t(
                                                            'eshop.product.table.selectAmount'
                                                        )}
                                                    </div>
                                                    {/* <span className="border rounded-full w-6 h-6 border-black flex items-center justify-center"> */}
                                                    {/*     i */}
                                                    {/* </span> */}
                                                </div>
                                            </td>
                                            <td
                                                className={clsx(
                                                    'rounded-br-lg pt-0',
                                                    isOpenedProduct
                                                        ? 'border-0 bg-cyan-50'
                                                        : 'border-0 bg-gray-light'
                                                )}
                                            >
                                                <div className="flex justify-end">
                                                    <BasketPlusMinus
                                                        catNo={catNo}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    <tr className="border-0">
                                        <td
                                            colSpan={3}
                                            className="h-2 border-0 p-0"
                                        ></td>
                                    </tr>
                                </React.Fragment>
                            )
                        })}
                </tbody>
            </table>
            <div
                className={clsx(
                    'flex justify-between my-4 px-4 transition-transform [transition-property:max-height] overflow-hidden text-md flex-col text-right',
                    sum[0] > 0 ? 'max-h-[100px]' : 'max-h-[0px]'
                )}
            >
                <span className="text-sm">
                    {t('eshop.product.detail.totalWithoutVat')}
                    {': '}
                    {format(sum[0].toFixed(2))}
                </span>
                <span className="font-bold">
                    <span className="text-sm">
                        {t('eshop.product.detail.totalWithVat')}
                        {': '}
                    </span>
                    {format(sum[1].toFixed(2))}
                </span>
            </div>
        </div>
    )
}
